import React, { useState, useEffect } from "react";
import { Modal, Form, InputNumber, Button, message } from "antd";
import API from "../../../api";
import dayjs from "dayjs";

const BalancePaidForm = ({ visible, onCancel, payment, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Calculate remaining balance with defensive check
  const remainingBalance =
    (payment?.payment?.balanceAmount || 0) -
    (Array.isArray(payment?.payment?.balancePaid)
      ? payment.payment.balancePaid.reduce(
          (sum, entry) => sum + (entry.amount || 0),
          0
        )
      : 0);

  useEffect(() => {
    // Prepopulate form fields
    form.setFieldsValue({
      amount: remainingBalance > 0 ? remainingBalance : 0,
      cash: remainingBalance > 0 ? remainingBalance : 0,
      upi: 0,
    });
  }, [payment, form, remainingBalance]);

  const handleCashChange = (cash) => {
    const amount = form.getFieldValue("amount") || 0;
    if (cash !== null && cash >= 0) {
      const upi = amount - cash >= 0 ? amount - cash : 0;
      form.setFieldsValue({ upi });
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const balancePaidEntry = {
        amount: values.amount,
        date: dayjs().toISOString(), // current date
        cash: values.cash,
        upi: values.upi,
      };

      const response = await API.put(`/rc-insurance-payments/${payment._id}`, {
        payment: {
          balancePaid: [balancePaidEntry],
        },
      });

      if (response.status === 200) {
        message.success("Balance payment added successfully");
        onSuccess(); // refresh
        form.resetFields();
        onCancel(); // close modal
      }
    } catch (error) {
      console.error("Error adding balance payment:", error);
      message.error("Failed to add balance payment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Add Balance Payment"
      open={visible}
      onCancel={onCancel}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          amount: remainingBalance > 0 ? remainingBalance : 0,
          cash: remainingBalance > 0 ? remainingBalance : 0,
          upi: 0,
        }}
      >
        {/* Amount Field */}
        <Form.Item
          label="Amount"
          name="amount"
          rules={[
            { required: true, message: "Please enter the amount" },
            { type: "number", min: 0, message: "Amount must be non-negative" },
            {
              validator: (_, value) =>
                value <= remainingBalance
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error("Amount cannot exceed remaining balance")
                    ),
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            onChange={(amount) => {
              // reset cash and upi when amount changes
              form.setFieldsValue({
                cash: amount || 0,
                upi: 0,
              });
            }}
          />
        </Form.Item>

        {/* Cash Field */}
        <Form.Item
          label="Cash"
          name="cash"
          rules={[
            { required: true, message: "Please enter the cash amount" },
            { type: "number", min: 0, message: "Cash must be non-negative" },
            {
              validator: (_, value) => {
                const amount = form.getFieldValue("amount") || 0;
                return value <= amount
                  ? Promise.resolve()
                  : Promise.reject(new Error("Cash cannot exceed the amount"));
              },
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            onChange={handleCashChange}
          />
        </Form.Item>

        {/* UPI Field */}
        <Form.Item
          label="UPI"
          name="upi"
          rules={[
            { required: true, message: "Please enter the UPI amount" },
            { type: "number", min: 0, message: "UPI must be non-negative" },
          ]}
        >
          <InputNumber style={{ width: "100%" }} min={0} disabled />
        </Form.Item>

        {/* Buttons */}
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            Submit
          </Button>
          <Button onClick={onCancel} style={{ marginLeft: 8 }}>
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default BalancePaidForm;
